import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Upload, ArrowRight, Star, Clock, Shield } from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';
import TurnstileWidget from '../components/TurnstileWidget';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cb900e0f-c443-41f6-9df3-17a2db93c215/artifacts/qd501ira_D70BB6EE-7344-46BE-86BC-6C6D197A54F6.png";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AuditPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    target_role: '',
    notes: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please fill in your name');
      return;
    }
    if (!formData.email.trim() || !EMAIL_RE.test(formData.email.trim())) {
      toast.error('Please provide a valid email address');
      return;
    }
    if (!turnstileToken) {
      toast.error('Please complete the security check');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('full_name', formData.name.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('target_role', formData.target_role.trim());
      submitData.append('notes', formData.notes.trim());
      submitData.append('source', 'audit');
      submitData.append('turnstile_token', turnstileToken);
      if (file) {
        submitData.append('resume', file);
      }

      const response = await fetch(`${API}/audit`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Submission failed');
      }

      setSubmitted(true);
      toast.success('Audit request submitted!');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-6">
        <Card className="max-w-lg p-8 rounded-3xl text-center animate-fade-in" data-testid="audit-success">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-teal-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Got It, {formData.name.split(' ')[0]}!
          </h1>
          
          <p className="text-slate-600 mb-8">
            We'll review your information and get back to you within 24-48 hours with personalized feedback.
          </p>

          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => navigate('/start')}
              className="bg-teal-600 hover:bg-teal-700 rounded-full py-6"
              data-testid="audit-success-start"
            >
              Start Full Intake
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600"
            >
              Back to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: Value Props */}
            <div className="lg:sticky lg:top-24">
              <Badge className="bg-teal-100 text-teal-700 mb-6">Free \u2022 No Obligation</Badge>
              
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                Get Your Free <br />
                <span className="text-gradient">Quick Resume Audit</span>
              </h1>
              
              <p className="text-slate-600 text-lg mb-8">
                Submit your details and we'll give you personalized feedback on how to improve your resume for ATS systems.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-teal-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">24-48 Hour Response</h4>
                    <p className="text-sm text-slate-600">Get expert feedback quickly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Star className="h-5 w-5 text-teal-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Expert Review</h4>
                    <p className="text-sm text-slate-600">Real human experts, not AI</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-teal-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">No Obligation</h4>
                    <p className="text-sm text-slate-600">Free advice, no pressure to buy</p>
                  </div>
                </div>
              </div>

              {/* Testimonial */}
              <Card className="p-6 rounded-2xl bg-white border border-slate-100">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 text-sm mb-3">
                  "The free audit alone was incredibly helpful. Showed me exactly what was wrong with my resume."
                </p>
                <p className="text-slate-900 font-semibold text-sm">\u2014 Rachel P.</p>
              </Card>
            </div>

            {/* Right: Form */}
            <Card className="p-8 rounded-3xl border border-slate-200 relative" data-testid="audit-form">
              <div className="logo-badge">
                <img src={LOGO_URL} alt="" className="h-8 w-8 rounded-lg" />
              </div>

              <form onSubmit={handleSubmit} className="pt-6 space-y-6">
                <div>
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    className="mt-2"
                    required
                    data-testid="audit-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="mt-2"
                    required
                    data-testid="audit-email"
                  />
                </div>

                <div>
                  <Label htmlFor="target_role">Target Role/Industry (Optional)</Label>
                  <Input
                    id="target_role"
                    value={formData.target_role}
                    onChange={(e) => setFormData({ ...formData, target_role: e.target.value })}
                    placeholder="e.g., Software Engineer, Marketing Manager"
                    className="mt-2"
                    data-testid="audit-role"
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Any specific concerns? (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Tell us about your situation, goals, or concerns..."
                    className="mt-2 min-h-[100px]"
                    data-testid="audit-notes"
                  />
                </div>

                <div>
                  <Label>Upload Resume (Optional)</Label>
                  <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-teal-300 transition-colors">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => setFile(e.target.files[0])}
                      className="hidden"
                      id="file-upload"
                      data-testid="audit-file"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      {file ? (
                        <p className="text-teal-700 font-medium">{file.name}</p>
                      ) : (
                        <>
                          <p className="text-slate-600 font-medium">Click to upload</p>
                          <p className="text-slate-400 text-sm">PDF, DOC, DOCX up to 10MB</p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <TurnstileWidget onVerify={setTurnstileToken} />

                <Button
                  type="submit"
                  className="w-full bg-teal-600 hover:bg-teal-700 rounded-full py-6 text-lg"
                  disabled={loading || !turnstileToken}
                  data-testid="audit-submit"
                >
                  {loading ? (
                    <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>Get Free Audit</>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500">
                  By submitting, you agree to our{' '}
                  <a href="/policies/privacy" className="text-teal-600 hover:underline">Privacy Policy</a>
                </p>
              </form>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
